export type Aggregation = "raw" | "10s" | "1m" | "5m" | "1h" | "1d";
export type AggMethod = "mean" | "max" | "min" | "first" | "last";

export interface PreprocessingParams {
  /** Pipe outer diameter, millimetres. Defaults to the drive record. */
  pipeOdMm?: number;
  /** Bulking factor baseline (TBM_BF in the old model). */
  bulkingFactorBaseline: number;
  /** Max allowed chainage increase between successive samples, metres. */
  chainageAllowableIncreaseM: number;
  /** Max allowed chainage decrease between successive samples, metres. */
  chainageAllowableDecreaseM: number;
  /** Drop samples where Torque==0 OR RPM==0 OR AdvanceRate==0. */
  zeroFilter: boolean;
  /** Filter to bypass_status == 2 (applies to Bulking Factor only). */
  miningOnly: boolean;
  aggregation: Aggregation;
  aggMethod: AggMethod;
  /**
   * Rolling-window outlier filter applied to Jacking Force.
   *
   * Inactivity gate (Zhang 2019, Erharter 2019): a JF sample is treated
   * as 'machine idle' and *masked before the rolling stats are computed*
   * if any of the following are true:
   *   - JF < `jackingMinKN`
   *   - AR == 0  ·  RPM == 0  ·  Torque == 0
   * Idle samples therefore don't pull the rolling mean toward zero or
   * inflate σ.
   *
   * After the inactivity mask, drop a JF sample when it lies more than
   * `jackingRollingSigma` standard deviations from the rolling mean of
   * the most recent `jackingRollingWindow` samples.
   */
  jackingRollingEnabled: boolean;
  jackingRollingWindow: number;
  jackingRollingSigma: number;
  jackingMinKN: number;
}

export const DEFAULT_PREPROCESSING: PreprocessingParams = {
  bulkingFactorBaseline: 1.0,
  chainageAllowableIncreaseM: 2,
  chainageAllowableDecreaseM: 0,
  zeroFilter: true,
  miningOnly: true,
  aggregation: "raw",
  aggMethod: "mean",
  jackingRollingEnabled: true,
  jackingRollingWindow: 50,
  jackingRollingSigma: 3,
  jackingMinKN: 50,
};

export function encodePreprocessing(p: PreprocessingParams): string {
  return JSON.stringify(p);
}

/**
 * The subset of PreprocessingParams that is saved globally on the drive
 * record and shared across every viewer. The remainder — aggregation
 * and aggMethod — stays session-local: it lives only in the browser's
 * preprocessing store and is never persisted.
 */
export type SavedPreprocessing = Pick<
  PreprocessingParams,
  | "bulkingFactorBaseline"
  | "chainageAllowableIncreaseM"
  | "chainageAllowableDecreaseM"
  | "zeroFilter"
  | "miningOnly"
  | "jackingRollingEnabled"
  | "jackingRollingWindow"
  | "jackingRollingSigma"
  | "jackingMinKN"
>;

/** Extract the globally-saved subset from a full PreprocessingParams. */
export function pickSavedPreprocessing(p: PreprocessingParams): SavedPreprocessing {
  return {
    bulkingFactorBaseline: p.bulkingFactorBaseline,
    chainageAllowableIncreaseM: p.chainageAllowableIncreaseM,
    chainageAllowableDecreaseM: p.chainageAllowableDecreaseM,
    zeroFilter: p.zeroFilter,
    miningOnly: p.miningOnly,
    jackingRollingEnabled: p.jackingRollingEnabled,
    jackingRollingWindow: p.jackingRollingWindow,
    jackingRollingSigma: p.jackingRollingSigma,
    jackingMinKN: p.jackingMinKN,
  };
}

export const DEFAULT_SAVED_PREPROCESSING: SavedPreprocessing =
  pickSavedPreprocessing(DEFAULT_PREPROCESSING);

/**
 * Coerce a stored (JSONB) value into a SavedPreprocessing, falling back
 * to defaults for anything missing or malformed.
 */
export function mergeSavedPreprocessing(raw: unknown): SavedPreprocessing {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_SAVED_PREPROCESSING };
  return { ...DEFAULT_SAVED_PREPROCESSING, ...(raw as Partial<SavedPreprocessing>) };
}

export const AGGREGATIONS: Aggregation[] = ["raw", "10s", "1m", "5m", "1h", "1d"];
export const AGG_METHODS: AggMethod[] = ["mean", "max", "min", "first", "last"];

/**
 * Coerce one decoded field onto the defaults. Every value that reaches
 * SQL (`aggregation`, `aggMethod`) is whitelisted rather than trusted:
 * an unknown `aggMethod` used to make `aggMethodSql` return `undefined`,
 * which was interpolated straight into the query and surfaced as a
 * Postgres syntax error (HTTP 500) instead of a 400. Numeric fields are
 * clamped so a stale or hand-edited querystring can't ask for a rolling
 * window of 10^9 samples.
 */
function coercePreprocessing(raw: Record<string, unknown>): PreprocessingParams {
  const out: PreprocessingParams = { ...DEFAULT_PREPROCESSING };
  const num = (v: unknown, min: number, max: number, fallback: number): number => {
    const n = Number(v);
    if (!Number.isFinite(n)) return fallback;
    return Math.min(max, Math.max(min, n));
  };
  const bool = (v: unknown, fallback: boolean): boolean =>
    typeof v === "boolean" ? v : fallback;

  if (raw.pipeOdMm !== undefined) {
    const n = Number(raw.pipeOdMm);
    if (Number.isFinite(n) && n > 0) out.pipeOdMm = n;
  }
  out.bulkingFactorBaseline = num(raw.bulkingFactorBaseline, 0, 1e6, DEFAULT_PREPROCESSING.bulkingFactorBaseline);
  out.chainageAllowableIncreaseM = num(raw.chainageAllowableIncreaseM, 0, 1e6, DEFAULT_PREPROCESSING.chainageAllowableIncreaseM);
  out.chainageAllowableDecreaseM = num(raw.chainageAllowableDecreaseM, 0, 1e6, DEFAULT_PREPROCESSING.chainageAllowableDecreaseM);
  out.zeroFilter = bool(raw.zeroFilter, DEFAULT_PREPROCESSING.zeroFilter);
  out.miningOnly = bool(raw.miningOnly, DEFAULT_PREPROCESSING.miningOnly);
  out.aggregation = AGGREGATIONS.includes(raw.aggregation as Aggregation)
    ? (raw.aggregation as Aggregation)
    : DEFAULT_PREPROCESSING.aggregation;
  out.aggMethod = AGG_METHODS.includes(raw.aggMethod as AggMethod)
    ? (raw.aggMethod as AggMethod)
    : DEFAULT_PREPROCESSING.aggMethod;
  out.jackingRollingEnabled = bool(raw.jackingRollingEnabled, DEFAULT_PREPROCESSING.jackingRollingEnabled);
  out.jackingRollingWindow = Math.round(
    num(raw.jackingRollingWindow, 2, 100_000, DEFAULT_PREPROCESSING.jackingRollingWindow),
  );
  out.jackingRollingSigma = num(raw.jackingRollingSigma, 0, 1000, DEFAULT_PREPROCESSING.jackingRollingSigma);
  out.jackingMinKN = num(raw.jackingMinKN, 0, 1e9, DEFAULT_PREPROCESSING.jackingMinKN);
  return out;
}

export function decodePreprocessing(raw: string | undefined | null): PreprocessingParams {
  if (!raw) return DEFAULT_PREPROCESSING;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return DEFAULT_PREPROCESSING;
    return coercePreprocessing(parsed as Record<string, unknown>);
  } catch {
    return DEFAULT_PREPROCESSING;
  }
}
